import {
  TELEMETRY_RATE_MAX_HZ,
  TELEMETRY_RATE_MIN_HZ,
  parseTelemetry,
  unwrapTelemetryPayload,
} from '@shared/index';
import type { Drone, DroneInfo, DroneTelemetry, Waypoint } from '@shared/types';
import { mixedContentProblem, restBaseUrl, webSocketUrl } from './endpoint';
import { api } from '../lib/api';
import { acquireDroneToken, authHeaders, invalidateDroneToken } from './token';

export type LinkPhase = 'idle' | 'connecting' | 'streaming' | 'polling' | 'retrying' | 'blocked' | 'stopped';

export interface DroneCapabilities {
  commands?: string[];
  fieldMap?: Record<string, string>;
  /**
   * `/info` 里声明的推流频率能力。
   *
   * 规范形态是 `{ min, max, default }`，但老固件会直接给一个数字；
   * 平台只把它当参考（轮询回退时用），真正生效的频率以 `Drone.telemetryRateHz` 为准。
   */
  telemetryRateHz?: number | { min?: number; max?: number; default?: number };
  [key: string]: unknown;
}

/** 链路快照：全部是可序列化的展示态，便于直接丢进 zustand。 */
export interface LinkSnapshot {
  droneId: string;
  phase: LinkPhase;
  transport: 'ws' | 'poll' | null;
  /** 面向操作员的链路说明（含失败原因） */
  message: string | null;
  retryAttempt: number;
  lastFrameAt: number | null;
  frameCount: number;
  /** 依据 seq 推断的丢帧数 */
  droppedFrames: number;
  issues: string[];
  info: DroneInfo | null;
  capabilities: DroneCapabilities | null;
}

export function initialLinkSnapshot(droneId: string): LinkSnapshot {
  return {
    droneId,
    phase: 'idle',
    transport: null,
    message: null,
    retryAttempt: 0,
    lastFrameAt: null,
    frameCount: 0,
    droppedFrames: 0,
    issues: [],
    info: null,
    capabilities: null,
  };
}

export interface DroneSessionOptions {
  onSnapshot: (patch: Partial<LinkSnapshot>) => void;
  /** 每解析出一帧就回调（同步，不经过 React） */
  onTelemetry: (telemetry: DroneTelemetry, issues: string[]) => void;
  /** 链路层捕获到的错误，交给外层决定是否提示 */
  onError?: (error: unknown, context: string) => void;
}

const MAX_BACKOFF_MS = 15_000;
const BASE_BACKOFF_MS = 1_000;
/** REST 轮询回退时的单次请求超时 */
const REST_TIMEOUT_MS = 4_000;

function clampRate(hz: number): number {
  if (!Number.isFinite(hz) || hz <= 0) return 5;
  return Math.min(TELEMETRY_RATE_MAX_HZ, Math.max(TELEMETRY_RATE_MIN_HZ, hz));
}

function backoffDelay(attempt: number): number {
  const base = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * 2 ** Math.min(attempt, 6));
  return base + Math.floor(Math.random() * 250);
}

/**
 * 单台无人机的实时链路。
 *
 * 传输策略：**WebSocket 优先，失败自动降级为 REST 轮询**，WS 恢复后自动切回。
 * 两条路径都产出同一份规范化遥测帧，上层不需要关心当前用的是哪种传输。
 *
 * 说明：这里只负责「连」与「解析」，不含任何 React 依赖，因此可以被单测直接驱动。
 */
export class DroneSession {
  private drone: Drone;
  private readonly options: DroneSessionOptions;
  private snapshot: LinkSnapshot;
  private socket: WebSocket | null = null;
  private pollTimer: number | null = null;
  private retryTimer: number | null = null;
  private heartbeatTimer: number | null = null;
  private stopped = true;
  private lastSeq = 0;
  /** 本轮连接尝试中无人机是否已明确拒绝凭据（401/403） */
  private authRejected = false;
  private readonly onVisibilityChange: () => void;

  constructor(drone: Drone, options: DroneSessionOptions) {
    this.drone = drone;
    this.options = options;
    this.snapshot = initialLinkSnapshot(drone.id);
    this.onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !this.stopped && this.socket === null) {
        // 后台标签页里浏览器会节流定时器，回到前台时立刻补一次连接
        this.connect();
      }
    };
  }

  get snapshotView(): LinkSnapshot {
    return this.snapshot;
  }

  private patch(patch: Partial<LinkSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    this.options.onSnapshot(patch);
  }

  private get effectiveFieldMap(): Record<string, string> {
    return { ...(this.snapshot.capabilities?.fieldMap ?? {}), ...this.drone.fieldMap };
  }

  private get rateHz(): number {
    return clampRate(this.drone.telemetryRateHz);
  }

  private get restBase(): string {
    return restBaseUrl(this.drone.endpoint);
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    void this.connect();
    // 心跳让平台侧的 last_seen 反映真实活跃度（不阻塞遥测）
    this.heartbeatTimer = window.setInterval(() => void this.reportSeen(), 30_000);
    void this.reportSeen();
  }

  stop(): void {
    this.stopped = true;
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.clearTimers();
    this.closeSocket();
    this.patch({ phase: 'stopped', transport: null, message: null });
  }

  /** 配置变更（地址、凭据、频率）后调用，用新配置重建链路。 */
  updateDrone(drone: Drone): void {
    const before = this.drone;
    this.drone = drone;
    const endpointChanged =
      before.endpoint.host !== drone.endpoint.host ||
      before.endpoint.port !== drone.endpoint.port ||
      before.endpoint.basePath !== drone.endpoint.basePath ||
      before.endpoint.wsPath !== drone.endpoint.wsPath ||
      before.endpoint.protocol !== drone.endpoint.protocol;
    const credentialChanged = before.credentialMode !== drone.credentialMode;

    if (!endpointChanged && !credentialChanged) {
      // 频率/阈值变化只需重建轮询定时器
      this.startPollingIfNeeded();
      return;
    }

    invalidateDroneToken(drone.id);
    this.patch(initialLinkSnapshot(drone.id));
    this.lastSeq = 0;
    this.clearTimers();
    this.closeSocket();
    if (!this.stopped) void this.connect();
  }

  private clearTimers(): void {
    if (this.pollTimer !== null) window.clearTimeout(this.pollTimer);
    if (this.retryTimer !== null) window.clearTimeout(this.retryTimer);
    if (this.heartbeatTimer !== null) window.clearInterval(this.heartbeatTimer);
    this.pollTimer = null;
    this.retryTimer = null;
    this.heartbeatTimer = null;
  }

  private closeSocket(): void {
    const socket = this.socket;
    this.socket = null;
    if (!socket) return;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;
    try {
      socket.close();
    } catch {
      /* 关闭失败无所谓 */
    }
  }

  private async connect(): Promise<void> {
    if (this.stopped) return;

    const problem = mixedContentProblem(this.drone.endpoint, window.location.protocol);
    if (problem) {
      this.patch({ phase: 'blocked', transport: null, message: problem });
      return;
    }

    this.patch({ phase: this.snapshot.frameCount === 0 ? 'connecting' : 'retrying', message: null });
    // 新一轮连接尝试：上一次的「凭据被拒」结论不再沿用，
    // 否则本轮重新签发的令牌也会被误当成拒绝状态。
    this.authRejected = false;

    let token: string | null = null;
    try {
      const access = await acquireDroneToken(this.drone.id, this.drone.credentialMode);
      token = access.token === '' ? null : access.token;
    } catch (error) {
      this.options.onError?.(error, '获取无人机访问令牌失败');
      this.patch({ message: '无法从平台获取无人机访问令牌，已改用无凭据方式尝试连接' });
    }

    if (this.stopped) return;

    // 先用 REST 拉一次：既能立刻出数据，也顺带验证地址与鉴权是否可用
    await this.refreshInfo();
    if (this.stopped) return;
    if (this.authRejected) return; // 凭据已被拒：不必再用同一个令牌去握手，退避后重新签发
    this.startPollingIfNeeded();
    await this.pollOnce();
    if (this.stopped) return;

    this.openSocket(token);
  }

  private openSocket(token: string | null): void {
    if (this.stopped || this.socket) return;

    let socket: WebSocket;
    try {
      socket = new WebSocket(webSocketUrl(this.drone.endpoint, token));
    } catch (error) {
      this.options.onError?.(error, '创建 WebSocket 失败');
      this.scheduleRetry('无法创建 WebSocket 连接');
      return;
    }

    this.socket = socket;

    socket.onopen = () => {
      if (this.stopped) return;
      this.stopPolling();
      // 新连接就是一条新的推流：无人机可能刚重启过，序号会从头开始。
      // 基线必须重置，否则旧流里的高位序号会一直掩盖新流里真正的丢帧。
      this.lastSeq = 0;
      this.patch({
        phase: 'streaming',
        transport: 'ws',
        message: null,
        retryAttempt: 0,
      });
    };

    socket.onmessage = (event) => {
      this.handleRawFrame(event.data);
    };

    socket.onerror = () => {
      // onclose 一定会紧接着触发，重连逻辑统一放在那里
    };

    socket.onclose = (event: CloseEvent) => {
      this.socket = null;
      if (this.stopped) return;
      // 4401 是无人机侧「凭据被拒」的约定关闭码（见 docs/drone-api.md）。
      // 它不是网络抖动，重连只会拿同一份被拒的凭据再撞一次门，
      // 因此必须走 rejectCredential：清缓存令牌 + 停止轮询 + 明确报鉴权失败。
      if (event.code === 4401) {
        this.rejectCredential(401, 'ws');
        return;
      }
      this.startPollingIfNeeded();
      this.scheduleRetry('遥测 WebSocket 已断开，正在重连（期间使用轮询回退）');
    };
  }

  private scheduleRetry(message: string, transport: 'ws' | 'poll' | null = 'poll'): void {
    if (this.stopped) return;
    const attempt = this.snapshot.retryAttempt + 1;
    this.patch({
      phase: 'retrying',
      transport,
      retryAttempt: attempt,
      message,
    });
    if (this.retryTimer !== null) window.clearTimeout(this.retryTimer);
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null;
      void this.connect();
    }, backoffDelay(attempt));
  }

  private startPollingIfNeeded(): void {
    if (this.stopped || this.authRejected || this.pollTimer !== null) return;
    const intervalMs = Math.max(120, Math.round(1000 / this.rateHz));
    this.patch({ transport: this.socket ? 'ws' : 'poll' });
    const tick = () => {
      this.pollTimer = window.setTimeout(() => {
        this.pollTimer = null;
        void this.pollOnce().finally(() => {
          // 凭据被拒时立刻停表：否则会以轮询频率反复重签令牌并撞击无人机。
          // WS 建立后也必须停表：两条链路同时取帧会让序号互相跳空，凭空产生丢帧。
          if (!this.stopped && !this.authRejected && this.socket === null) tick();
        });
      }, intervalMs);
    };
    tick();
  }

  private stopPolling(): void {
    if (this.pollTimer !== null) {
      window.clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private async fetchWithTimeout(url: string, init: RequestInit): Promise<Response> {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), REST_TIMEOUT_MS);
    try {
      return await fetch(url, { ...init, signal: controller.signal, cache: 'no-store' });
    } finally {
      window.clearTimeout(timer);
    }
  }

  private async authorizedInit(): Promise<RequestInit> {
    const access = await acquireDroneToken(this.drone.id, this.drone.credentialMode);
    return { headers: { Accept: 'application/json', ...authHeaders(access) } };
  }

  private async pollOnce(): Promise<void> {
    if (this.stopped || this.authRejected) return;
    try {
      const init = await this.authorizedInit();
      const response = await this.fetchWithTimeout(`${this.restBase}/telemetry`, init);
      if (response.status === 401 || response.status === 403) {
        this.rejectCredential(response.status);
        return;
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const body: unknown = await response.json();
      this.ingest(body, 'poll');
    } catch (error) {
      if (this.socket) return; // WS 正常时不因轮询失败而报警
      this.options.onError?.(error, '拉取遥测失败');
      this.patch({ phase: this.snapshot.frameCount === 0 ? 'retrying' : 'polling', message: '轮询遥测失败，正在重试' });
    }
  }

  /**
   * 无人机明确拒绝凭据（401/403）。
   *
   * 这和「网络不通」是两回事，必须做三件事：
   * 1. 丢掉缓存令牌，让下一次连接重新签发；
   * 2. 停掉轮询并改走退避重试——按轮询频率反复重试同一个被拒凭据
   *    会以每秒数次的频率重复签发令牌并撞击无人机；
   * 3. 在界面上说清是鉴权问题，否则操作员会去查信号和端口。
   */
  private rejectCredential(status: number, via: 'http' | 'ws' = 'http'): void {
    if (this.authRejected) return;
    this.authRejected = true;
    invalidateDroneToken(this.drone.id);
    this.stopPolling();
    // WS 侧拿不到 HTTP 状态码，只有约定的关闭码 4401，报错文案要照实写
    const reason = via === 'ws' ? 'WebSocket 4401' : `HTTP ${status}`;
    const detail = `无人机拒绝接入凭据（${reason}），已清除缓存令牌，重新签发后重试`;
    this.options.onError?.(new Error(`无人机拒绝接入凭据（${reason}）`), '接入鉴权失败');
    if (this.socket) {
      // WS 仍在推流时不改链路状态，只留下说明
      this.patch({ message: detail });
      return;
    }
    this.scheduleRetry(detail, null);
  }

  private async refreshInfo(): Promise<void> {
    try {
      const init = await this.authorizedInit();
      const response = await this.fetchWithTimeout(`${this.restBase}/info`, init);
      if (response.status === 401 || response.status === 403) {
        this.rejectCredential(response.status);
        return;
      }
      if (!response.ok) return;
      const body = unwrapTelemetryPayload(await response.json()) as Record<string, unknown> | null;
      if (!body || typeof body !== 'object') return;
      const info = body as unknown as DroneInfo & { capabilities?: DroneCapabilities };
      this.patch({
        info: {
          droneId: this.drone.id,
          uniqueId: String(info.uniqueId ?? this.drone.uniqueId),
          name: String(info.name ?? this.drone.name),
          model: String(info.model ?? this.drone.model),
          vendor: String(info.vendor ?? this.drone.vendor),
          firmwareVersion: String(info.firmwareVersion ?? '未知'),
          uptimeSec: Number(info.uptimeSec ?? 0),
          home: info.home ?? null,
        },
        capabilities: (info.capabilities ?? null) as DroneCapabilities | null,
      });
    } catch {
      // /info 拉不到不影响遥测主链路
    }
  }

  private async reportSeen(): Promise<void> {
    try {
      await api.post<void>(`/api/drones/${this.drone.id}/seen`, {});
    } catch {
      // 心跳失败无需打扰操作员
    }
  }

  private handleRawFrame(data: unknown): void {
    if (typeof data !== 'string') return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(data);
    } catch {
      this.options.onError?.(new Error('遥测帧不是合法 JSON'), '解析 WebSocket 消息失败');
      return;
    }
    this.ingest(parsed, 'ws');
  }

  /** 把任意形态的报文（单帧 / 数组 / 包裹对象）统一解析并派发。 */
  ingest(body: unknown, transport: 'ws' | 'poll'): void {
    const unwrapped = unwrapTelemetryPayload(body);
    const frames: unknown[] = Array.isArray(unwrapped) ? unwrapped : [unwrapped];

    for (const frame of frames) {
      const result = parseTelemetry(frame, {
        droneId: this.drone.id,
        fieldMap: this.effectiveFieldMap,
        transport,
        home: this.snapshot.info?.home ?? null,
      });

      const seq = result.telemetry.seq;
      let dropped = this.snapshot.droppedFrames;
      if (seq < this.lastSeq) {
        // 序号回退说明对端换了流（重启 / 换源），重建基线，不要把它算成丢帧
        this.lastSeq = 0;
      } else if (this.lastSeq > 0 && seq > this.lastSeq + 1) {
        dropped += seq - this.lastSeq - 1;
      }
      this.lastSeq = Math.max(this.lastSeq, seq);

      // 链路相位与传输方式主要由连接生命周期决定（onopen / onclose / 重试）。
      // 轮询回退路径没有 onopen，所以这里补一次；但 WS 已建立时，
      // 在途的轮询响应不能把正在推流的链路降级显示成轮询。
      const onSocket = this.socket !== null;
      this.patch({
        phase: transport === 'ws' || onSocket ? 'streaming' : 'polling',
        transport: onSocket ? 'ws' : transport,
        lastFrameAt: result.telemetry.receivedAt,
        frameCount: this.snapshot.frameCount + 1,
        droppedFrames: dropped,
        issues: result.issues,
      });

      this.options.onTelemetry(result.telemetry, result.issues);
    }
  }

  /** 下发一条指令，返回无人机侧的响应体。 */
  async sendCommand(command: string, payload: Record<string, unknown>): Promise<unknown> {
    const init = await this.authorizedInit();
    const response = await this.fetchWithTimeout(`${this.restBase}/commands`, {
      ...init,
      method: 'POST',
      headers: { ...(init.headers as Record<string, string>), 'Content-Type': 'application/json' },
      body: JSON.stringify({ command, ...payload }),
    });

    const text = await response.text();
    const parsed = text.trim() === '' ? null : safeJson(text);
    if (!response.ok) {
      const message =
        (parsed as { error?: { message?: string } } | null)?.error?.message ??
        `无人机拒绝了该指令（HTTP ${response.status}）`;
      throw new Error(message);
    }
    return unwrapTelemetryPayload(parsed);
  }

  async fetchMission(): Promise<{ waypoints: Waypoint[]; missionId: string | null }> {
    const init = await this.authorizedInit();
    const response = await this.fetchWithTimeout(`${this.restBase}/mission`, init);
    if (!response.ok) throw new Error(`读取航线失败（HTTP ${response.status}）`);
    const body = unwrapTelemetryPayload(await response.json()) as Record<string, unknown> | null;
    const list = Array.isArray(body?.waypoints) ? (body!.waypoints as Waypoint[]) : [];
    return { waypoints: list, missionId: (body?.missionId as string | null) ?? null };
  }

  async uploadMission(waypoints: Waypoint[]): Promise<unknown> {
    const init = await this.authorizedInit();
    const response = await this.fetchWithTimeout(`${this.restBase}/mission`, {
      ...init,
      method: 'POST',
      headers: { ...(init.headers as Record<string, string>), 'Content-Type': 'application/json' },
      body: JSON.stringify({ waypoints }),
    });
    const text = await response.text();
    const parsed = text.trim() === '' ? null : safeJson(text);
    if (!response.ok) {
      const message =
        (parsed as { error?: { message?: string } } | null)?.error?.message ??
        `上传航线失败（HTTP ${response.status}）`;
      throw new Error(message);
    }
    return unwrapTelemetryPayload(parsed);
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
